(function () {
  'use strict';

  /* =========================================================
     Shared helper: sync `inert` (or tabindex fallback) with
     an element's aria-hidden state. Used by both the Flickity
     slideshow fix and the hdt-reveal blog-carousel fix.

     FIX (2026-07-09): Some Flickity carousels (e.g. the category
     nav strip showing Sofas/Tables/Chairs...) mark every
     non-"is-selected" slide with aria-hidden="true" even though
     multiple slides remain visibly on-screen at once. Blindly
     applying `inert` to every aria-hidden element made those
     visible-but-"hidden" slides unclickable. We now only treat
     an element as truly hidden (and therefore inert-able) if it
     is ALSO outside the visible viewport.
     ========================================================= */
  var supportsInert = 'inert' in HTMLElement.prototype;

  // Find the nearest ancestor that actually clips overflow (the real
  // "viewport" a carousel slide is visible within). On mobile, Flickity
  // carousels show one slide at a time with neighbors peeking at the
  // edges — those peeking/off-view slides sit within the *browser
  // window* bounds but are clipped out by this ancestor's
  // overflow:hidden. Checking against the window alone (old approach)
  // wrongly treated them as visible, which un-inerted them and let
  // them intercept touch events meant for the drag gesture, breaking
  // swipe on mobile. Checking against this clipping ancestor instead
  // fixes both the desktop multi-item strip case and the mobile
  // single-slide swipe case.
  function getClippingAncestorRect(el) {
    var node = el.parentElement;
    while (node && node !== document.documentElement) {
      var style = window.getComputedStyle(node);
      if (style.overflow === 'hidden' || style.overflowX === 'hidden' ||
          style.overflow === 'clip' || style.overflowX === 'clip') {
        return node.getBoundingClientRect();
      }
      node = node.parentElement;
    }
    return null;
  }

  function isVisiblyOnScreen(el) {
    var rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    var clipRect = getClippingAncestorRect(el);
    if (clipRect) {
      return rect.right > clipRect.left && rect.left < clipRect.right &&
             rect.bottom > clipRect.top && rect.top < clipRect.bottom;
    }

    // Fallback: no clipping ancestor found, use the browser window.
    var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.right > 0 && rect.left < viewportWidth &&
           rect.bottom > 0 && rect.top < viewportHeight;
  }

  function syncInert(el) {
    var isHidden = el.getAttribute('aria-hidden') === 'true';

    // Only actually treat as hidden if it's also off-screen.
    // A slide that's aria-hidden but still visible on screen
    // (e.g. multi-item carousel strips) should stay interactive.
    if (isHidden && isVisiblyOnScreen(el)) {
      isHidden = false;
    }

    if (supportsInert) {
      if (isHidden) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
      return;
    }

    if (isHidden) {
      el.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')
        .forEach(function (f) {
          if (!f.hasAttribute('data-prev-tabindex')) {
            f.setAttribute('data-prev-tabindex', f.getAttribute('tabindex') || '');
          }
          f.setAttribute('tabindex', '-1');
        });
    } else {
      el.querySelectorAll('[data-prev-tabindex]').forEach(function (f) {
        var prev = f.getAttribute('data-prev-tabindex');
        prev ? f.setAttribute('tabindex', prev) : f.removeAttribute('tabindex');
        f.removeAttribute('data-prev-tabindex');
      });
    }
  }

  // Batch rapid aria-hidden churn (e.g. during a drag) into one pass per frame.
  var pendingInert = new Set();
  var inertScheduled = false;
  function scheduleInert(el) {
    pendingInert.add(el);
    if (inertScheduled) return;
    inertScheduled = true;
    requestAnimationFrame(function () {
      pendingInert.forEach(syncInert);
      pendingInert.clear();
      inertScheduled = false;
    });
  }

  function watchInert(el) {
    if (el.__inertWatched) return;
    el.__inertWatched = true;
    syncInert(el);
    new MutationObserver(function () { scheduleInert(el); })
      .observe(el, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  /* =========================================================
     1. Flickity slideshow slides: aria-hidden must not be
        focusable. Re-synced on Flickity's own lifecycle events
        (cheap: only fires on real transitions, not every drag frame),
        and also on scroll/resize since visibility can change
        without an aria-hidden mutation firing.
     ========================================================= */
  function initFlickitySlides(root) {
    (root || document).querySelectorAll('.flickityt4s [aria-hidden]').forEach(watchInert);
  }
  ['select.flickityt4s', 'settle.flickityt4s', 'ready.flickityt4s'].forEach(function (evt) {
    document.addEventListener(evt, function () { initFlickitySlides(document); });
  });

  // Re-check visibility-based inert state on resize (layout can reflow
  // multi-item strips so more/fewer slides become visible).
  var resizeScheduled = false;
  window.addEventListener('resize', function () {
    if (resizeScheduled) return;
    resizeScheduled = true;
    requestAnimationFrame(function () {
      document.querySelectorAll('.flickityt4s [aria-hidden]').forEach(syncInert);
      resizeScheduled = false;
    });
  });

  /* =========================================================
     2. hdt-reveal blog carousel slides — same aria-hidden issue,
        scoped to this specific markup only.
     ========================================================= */
  function initHdtReveal(root) {
    (root || document).querySelectorAll('[hdt-reveal]').forEach(watchInert);
  }

  /* =========================================================
     3. jdgm-carousel product links missing an accessible name
     ========================================================= */
  function fixJdgmLinks(root) {
    root.querySelectorAll('a.jdgm-carousel-item_product').forEach(function (a) {
      if (a.getAttribute('aria-label')) return;
      var text = a.textContent.trim();
      if (text) {
        a.setAttribute('aria-label', text);
        return;
      }
      var href = a.getAttribute('href') || '';
      var match = href.match(/\/products\/([^/#?]+)/);
      if (match) a.setAttribute('aria-label', match[1].replace(/-/g, ' '));
    });
  }

  /* =========================================================
     4. Judge.me star rating <a> -> <button>
        (only needed on the customer-reviews page)
     ========================================================= */
  function fixJdgmStars(root) {
    root.querySelectorAll('a.jdgm-star').forEach(function (a) {
      if (a.dataset.jdgmFixed) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      Array.prototype.forEach.call(a.attributes, function (attr) {
        if (attr.name === 'href' || attr.name === 'target') return;
        btn.setAttribute(attr.name, attr.value);
      });
      btn.setAttribute('data-jdgm-fixed', 'true');
      btn.innerHTML = a.innerHTML;
      a.replaceWith(btn);
    });
  }

  /* =========================================================
     5. Growave carousel prev/next buttons
     ========================================================= */
  function fixGowlNavButtons(root) {
    root.querySelectorAll('button.gowl-prev').forEach(function (btn) {
      btn.removeAttribute('role');
      if (!btn.getAttribute('aria-label')) btn.setAttribute('aria-label', 'Prev');
    });
    root.querySelectorAll('button.gowl-next').forEach(function (btn) {
      btn.removeAttribute('role');
      if (!btn.getAttribute('aria-label')) btn.setAttribute('aria-label', 'Next');
    });
  }

  /* =========================================================
     6. Growave "recently viewed" product links missing a name
     ========================================================= */
  function fixGowlProductLinks(root) {
    root.querySelectorAll('.ga-product a[href^="/products/"]').forEach(function (a) {
      if (a.getAttribute('aria-label')) return;
      var match = a.getAttribute('href').match(/\/products\/([^/#?]+)/);
      if (match) a.setAttribute('aria-label', match[1].replace(/-/g, ' '));
    });
  }

  /* =========================================================
     7. Zip installment logo missing/empty alt
        - Treats alt="" the same as a missing alt (fixed bug)
        - Also watches each img individually in case Zip rewrites
          its attributes in place later without adding a new node
          (fixed the "not effective" issue)
     ========================================================= */
  function fixZipLogoAlt(root) {
    root.querySelectorAll('img.zip-logo').forEach(function (img) {
      var alt = img.getAttribute('alt');
      if (!alt || !alt.trim()) img.setAttribute('alt', 'Zip');

      if (!img.__zipAltWatched) {
        img.__zipAltWatched = true;
        new MutationObserver(function () {
          var a = img.getAttribute('alt');
          if (!a || !a.trim()) img.setAttribute('alt', 'Zip');
        }).observe(img, { attributes: true, attributeFilter: ['alt'] });
      }
    });
  }

  /* =========================================================
     8. jdgm-carousel product links (double-underscore variant)
        and Judge.me review-picture links missing an accessible name
     ========================================================= */
  function fixJdgmCarouselLinksDU(root) {
    (root || document).querySelectorAll('a.jdgm-carousel-item__product').forEach(function (link) {
      var titleEl = link.querySelector('.jdgm-carousel-item__product-title');
      var text = titleEl ? titleEl.textContent.trim() : link.textContent.trim();
      if (text && link.getAttribute('aria-label') !== text) {
        link.setAttribute('aria-label', text);
      }
    });
  }

  function fixJdgmReviewPicLinks(root) {
    (root || document).querySelectorAll('a.jdgm-rev__pic-link').forEach(function (link) {
      if (!link.getAttribute('aria-label')) {
        link.setAttribute('aria-label', 'Buyer review image');
      }
      var img = link.querySelector('img');
      if (img && !img.alt) {
        img.alt = 'Buyer review image';
      }
    });
  }

  /* =========================================================
     Run once for anything already in the DOM at load time
     ========================================================= */
  document.addEventListener('DOMContentLoaded', function () {
    initFlickitySlides(document);
    initHdtReveal(document);
    fixJdgmLinks(document);
    fixJdgmStars(document);
    fixGowlNavButtons(document);
    fixGowlProductLinks(document);
    fixZipLogoAlt(document);
    fixJdgmCarouselLinksDU(document);
    fixJdgmReviewPicLinks(document);
  });

  /* =========================================================
     One shared observer for anything added to the DOM later
     (widgets that render asynchronously). Only the nodes that
     were actually added get checked — no full-document rescans —
     and work is batched per animation frame to avoid competing
     with carousel drag/transition performance.
     ========================================================= */
  var lookups = [
    { selector: '.flickityt4s [aria-hidden]', fix: initFlickitySlides },
    { selector: '[hdt-reveal]', fix: initHdtReveal },
    { selector: 'a.jdgm-carousel-item_product', fix: fixJdgmLinks },
    { selector: 'a.jdgm-star', fix: fixJdgmStars },
    { selector: 'button.gowl-prev, button.gowl-next', fix: fixGowlNavButtons },
    { selector: '.ga-product a[href^="/products/"]', fix: fixGowlProductLinks },
    { selector: 'img.zip-logo', fix: fixZipLogoAlt },
    { selector: 'a.jdgm-carousel-item__product', fix: fixJdgmCarouselLinksDU },
    { selector: 'a.jdgm-rev__pic-link', fix: fixJdgmReviewPicLinks }
  ];

  var pendingNodes = new Set();
  var nodesScheduled = false;
  function scheduleNode(node) {
    pendingNodes.add(node);
    if (nodesScheduled) return;
    nodesScheduled = true;
    requestAnimationFrame(function () {
      pendingNodes.forEach(function (n) {
        lookups.forEach(function (l) {
          if (n.matches && n.matches(l.selector)) {
            l.fix(n.parentNode || document);
          } else if (n.querySelector && n.querySelector(l.selector)) {
            l.fix(n);
          }
        });
      });
      pendingNodes.clear();
      nodesScheduled = false;
    });
  }

  var globalObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        scheduleNode(node);
      });
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    globalObserver.observe(document.body, { childList: true, subtree: true });
  });
})();
